import { useState } from 'react';
import {
  Box,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Loading } from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import {
  GRAIN_OPTIONS,
  PERIOD_OPTIONS,
  type EarningsPeriod,
  type EntityGrain,
} from './earningsModel';
import { EarningsBand } from './EarningsBand';
import { useEarnings } from './useEarnings';

/**
 * Earnings - the first analytical surface (Reports > Earnings, /earnings).
 *
 * It answers, per entity, which merchants and networks make us money. One
 * full-width band renders per market the current SCOPE derives to (UK -> one
 * band in GBP, US -> one in USD, GLOBAL -> UK then US stacked), so it keys off
 * the region scope, not the switcher. There is NO cross-currency grand total.
 *
 * Read-only: no provider mutations, so the audit floor stays dormant. All margin
 * comes through the earnings aggregation seam (`useEarnings` -> the earnings
 * module); the surface never touches a raw transaction (D-earnings).
 */

/** The surface without the access wrapper. Exported so tests can inject `asOf`. */
export const EarningsView = ({ asOf }: { asOf?: number }) => {
  // One reference clock for the period windows and the trend, captured once via
  // a lazy initializer (so the render body stays pure). Tests inject a fixed
  // value for determinism.
  const [now] = useState<number>(() => asOf ?? Date.now());
  const [grain, setGrain] = useState<EntityGrain>('network');
  const [period, setPeriod] = useState<EarningsPeriod>('mtd');

  const { view, isLoading } = useEarnings({ grain, period, now });

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 1 }}
      >
        Reports
      </Typography>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Earnings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Which merchants and networks make us money. Margin is what the network
        paid us minus what the member earns.
      </Typography>

      {/* Page-level controls. The grain and period govern the cards and the
          table for every band; the trend is decoupled (a fixed monthly window).
          One control set governs both bands so a GLOBAL view stays coherent. */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          mb: 3,
        }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={grain}
          onChange={(_e, next: EntityGrain | null) => next && setGrain(next)}
          aria-label="entity grain"
        >
          {GRAIN_OPTIONS.map((o) => (
            <ToggleButton key={o.id} value={o.id}>
              {o.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <TextField
          select
          size="small"
          label="Period"
          value={period}
          onChange={(e) => setPeriod(e.target.value as EarningsPeriod)}
          sx={{ minWidth: 220 }}
        >
          {PERIOD_OPTIONS.map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {isLoading || !view ? (
        <Loading />
      ) : view.bands.length === 0 ? (
        <Typography color="text.secondary">
          No market is in scope for this account.
        </Typography>
      ) : (
        view.bands.map((band) => <EarningsBand key={band.market} band={band} />)
      )}
    </Box>
  );
};

/**
 * Gated entry registered at /earnings. `earnings:read` is a read capability, so
 * `viewer` (read everywhere) and `super_admin` see it; the descriptive `editor`
 * and the placeholder support / fraud roles do not - margin is sensitive
 * financial data, kept off the descriptive-editing trust line (D-earnings).
 */
export const Earnings = () => (
  <RequireAccess capability="earnings:read">
    <EarningsView />
  </RequireAccess>
);
